import { JsonLd } from '@/lib/shared/components/JsonLd';

import { BASE_FRONT_URL } from '@/lib/config';
import Head from 'next/head';

import { CurrencySymbol } from '../../currency';
import { FallbackSeo, PageSeo } from '../model';

type Props = {
  seo: Omit<PageSeo, 'language' | 'path'> | null;
  fallback: FallbackSeo;
  language: string;
  noFollow?: boolean;
  robots?: string;
  meta: {
    minPrice?: number;
    currency?: CurrencySymbol;
    quantity: number;
  };
  url: string;
};

export const replace = (str: string, meta: Props['meta']) => {
  return str
    .replace(
      '%minPrice%',
      meta.minPrice && meta.currency
        ? meta.currency + meta.minPrice.toString()
        : ''
    )
    .replace('%quantity%', meta.quantity.toString());
};

export const CatalogSeoEntries = ({
  seo,
  fallback,
  language,
  noFollow,
  robots,
  meta,
  url,
}: Props) => {
  const title = seo?.title ? replace(seo.title, meta) : fallback.title;
  const description = seo?.description
    ? replace(seo.description, meta)
    : fallback.description;
  const rawOpenGraphImage = seo?.openGraphImage || fallback.openGraphImage;
  const openGraphImage = rawOpenGraphImage?.startsWith('/')
    ? `${BASE_FRONT_URL}${rawOpenGraphImage}`
    : rawOpenGraphImage;
  const keywords = seo?.keywords || fallback.keywords;
  const cleanUrl = url.replaceAll(/&page=\d+|\?page=\d+/gi, '');
  return (
    <Head>
      <title>{title}</title>
      <meta property="og:image" content={openGraphImage} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image:width" content="510" />
      <meta property="og:site_name" content="Villacarte" />
      <meta property="og:url" content={BASE_FRONT_URL} />
      <link
        rel="canonical"
        href={`${BASE_FRONT_URL}/${language}${cleanUrl}`}
      ></link>
      <meta
        name="cc:attributionURL"
        content={`${BASE_FRONT_URL}/${language}${cleanUrl}`}
      />
      <link
        rel="alternate"
        href={`${BASE_FRONT_URL}/en${cleanUrl}`}
        hrefLang="en"
      />
      <link
        rel="alternate"
        href={`${BASE_FRONT_URL}/ru${cleanUrl}`}
        hrefLang="ru"
      />
      <link
        rel="alternate"
        href={`${BASE_FRONT_URL}/en${cleanUrl}`}
        hrefLang="x-default"
      />
      {robots ? (
        <meta name="robots" content={robots} />
      ) : (
        noFollow && <meta name="robots" content="noindex, follow" />
      )}
      {seo?.jsonLd && <JsonLd data={seo.jsonLd} />}
    </Head>
  );
};
